import type { LocationSlot } from './types.js';
import {
  getCurrentOffset, getAbbr, getDSTRules, toLocal, formatLocalTime, formatOffset,
} from './timezone.js';
import type { DSTRule } from './timezone.js';

// === Calendar invite export ===
//
// The single most common cross-timezone failure is an invite that displays in
// the sender's zone rather than the recipient's. Two defences, both applied here:
//
//   1. Every local time is spelled out in the DESCRIPTION as plain text, so the
//      invite stays legible even if a client mishandles the timezone metadata.
//   2. Recurring invites carry a real VTIMEZONE with the anchor zone's DST rules,
//      so the meeting follows the lead time zone the way the organiser intended
//      instead of drifting or being pinned to a fixed UTC instant.

export interface IcsOptions {
  slots: LocationSlot[];
  /** UTC hour of the meeting, 0–23 (may be fractional) */
  utcHour: number;
  /** Calendar date of the meeting, interpreted as a UTC day */
  date: Date;
  durationMinutes: number;
  title: string;
  /**
   * Slot index whose local time the meeting is pinned to. Recurring events
   * follow this zone's DST rules. Pass null to pin to a fixed UTC instant.
   */
  anchorIndex: number | null;
  recurring: boolean;
}

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/** Format a UTC offset in hours as the iCalendar "+0530" form */
function icsOffset(offset: number): string {
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  const hours = Math.floor(abs);
  const minutes = Math.round((abs - hours) * 60);
  return `${sign}${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
}

function icsRRule(rule: DSTRule): string {
  const day = rule.nth === 'last' ? `-1${DAY_CODES[rule.weekday]}` : `${rule.nth}${DAY_CODES[rule.weekday]}`;
  return `FREQ=YEARLY;BYMONTH=${rule.month};BYDAY=${day}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** "20260316T140000" from date parts */
function icsDateTime(y: number, mo: number, d: number, h: number, mi: number): string {
  return `${y}${pad2(mo)}${pad2(d)}T${pad2(h)}${pad2(mi)}00`;
}

/** Escape a text value per RFC 5545 */
function esc(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/**
 * Fold lines to 75 octets per RFC 5545.
 *
 * The limit is octets, not characters, and descriptions carry non-ASCII text
 * (em dashes, accented city names). Folding is therefore done over UTF-8 byte
 * lengths, and never inside a multi-byte character — splitting one would
 * corrupt the file.
 */
function fold(line: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;

  const out: string[] = [];
  let current = '';
  let bytes = 0;
  // Continuation lines start with a space, which consumes one of the 75 octets.
  let limit = 75;

  for (const ch of line) {
    const size = encoder.encode(ch).length;
    if (bytes + size > limit) {
      out.push(current);
      current = ch;
      bytes = size;
      limit = 74;
    } else {
      current += ch;
      bytes += size;
    }
  }
  if (current) out.push(current);

  return out.map((part, i) => (i === 0 ? part : ' ' + part)).join('\r\n');
}

/** Build a VTIMEZONE block for a zone, using its inferred DST rules */
function buildVTimezone(slot: LocationSlot): string[] {
  const tz = slot.timezone;
  const rules = getDSTRules(tz);
  const lines: string[] = ['BEGIN:VTIMEZONE', `TZID:${tz.id}`];

  if (!rules) {
    // Zone without DST: a single STANDARD component with a fixed offset.
    lines.push(
      'BEGIN:STANDARD',
      `TZOFFSETFROM:${icsOffset(tz.utc_offset)}`,
      `TZOFFSETTO:${icsOffset(tz.utc_offset)}`,
      `TZNAME:${tz.abbr}`,
      'DTSTART:19700101T000000',
      'END:STANDARD'
    );
  } else {
    const std = tz.utc_offset;
    const dst = tz.utc_offset + (tz.dst_offset ?? 1);
    lines.push(
      'BEGIN:DAYLIGHT',
      `TZOFFSETFROM:${icsOffset(std)}`,
      `TZOFFSETTO:${icsOffset(dst)}`,
      `TZNAME:${tz.abbr_dst ?? tz.abbr}`,
      `DTSTART:19700101T${pad2(rules.start.hour)}${pad2(rules.start.minute)}00`,
      `RRULE:${icsRRule(rules.start)}`,
      'END:DAYLIGHT',
      'BEGIN:STANDARD',
      `TZOFFSETFROM:${icsOffset(dst)}`,
      `TZOFFSETTO:${icsOffset(std)}`,
      `TZNAME:${tz.abbr}`,
      `DTSTART:19700101T${pad2(rules.end.hour)}${pad2(rules.end.minute)}00`,
      `RRULE:${icsRRule(rules.end)}`,
      'END:STANDARD'
    );
  }

  lines.push('END:VTIMEZONE');
  return lines;
}

/** The exact instant of the meeting */
function meetingInstant(date: Date, utcHour: number): Date {
  const totalMinutes = Math.round(utcHour * 60);
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, totalMinutes)
  );
}

/**
 * A one-line summary listing every participant's local time.
 *
 * Written to be pasted directly into an invite body or chat message, which is
 * the recommended defence against a client rendering the invite in the wrong zone.
 */
export function buildInviteLine(slots: LocationSlot[], utcHour: number, date: Date): string {
  const at = meetingInstant(date, utcHour);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const datePart = `${days[at.getUTCDay()]}, ${months[at.getUTCMonth()]} ${at.getUTCDate()}`;

  const parts = slots.map(slot => {
    const offset = getCurrentOffset(slot.timezone, at);
    const local = toLocal(utcHour, offset);
    return `${formatLocalTime(local)} ${getAbbr(slot.timezone, at)}`;
  });

  const utcMinutes = Math.round((utcHour % 24) * 60);
  const utcLabel = `${pad2(Math.floor(utcMinutes / 60))}:${pad2(utcMinutes % 60)} UTC`;

  return `${datePart} · ${parts.join(' / ')} (${utcLabel})`;
}

/** A multi-line breakdown of every participant's local time */
export function buildTimeBreakdown(slots: LocationSlot[], utcHour: number, date: Date): string {
  const at = meetingInstant(date, utcHour);
  const utcMinutes = Math.round((utcHour % 24) * 60);

  const lines = [`${pad2(Math.floor(utcMinutes / 60))}:${pad2(utcMinutes % 60)} UTC`];
  for (const slot of slots) {
    const offset = getCurrentOffset(slot.timezone, at);
    const local = toLocal(utcHour, offset);
    const who = slot.label === slot.city.name ? slot.city.name : `${slot.label} (${slot.city.name})`;
    lines.push(`  ${who}: ${formatLocalTime(local)} ${getAbbr(slot.timezone, at)} — ${formatOffset(offset)}`);
  }
  return lines.join('\n');
}

/** Build a complete .ics document */
export function buildIcs(opts: IcsOptions): string {
  const { slots, utcHour, date, durationMinutes, title, anchorIndex, recurring } = opts;
  const at = meetingInstant(date, utcHour);
  const anchor = anchorIndex === null ? null : slots[anchorIndex] ?? null;

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Time Zone Meeting Planner//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  if (anchor) lines.push(...buildVTimezone(anchor));

  lines.push('BEGIN:VEVENT');
  lines.push(`UID:${uid(at)}`);
  lines.push(`DTSTAMP:${icsUtc(new Date())}`);

  if (anchor) {
    // Anchored: express the start in the lead time zone so recurrences follow
    // that zone's DST rules rather than drifting against them.
    const offset = getCurrentOffset(anchor.timezone, at);
    const localMs = at.getTime() + offset * 3600000;
    const l = new Date(localMs);
    const start = icsDateTime(
      l.getUTCFullYear(), l.getUTCMonth() + 1, l.getUTCDate(), l.getUTCHours(), l.getUTCMinutes()
    );
    const endMs = localMs + durationMinutes * 60000;
    const e = new Date(endMs);
    const end = icsDateTime(
      e.getUTCFullYear(), e.getUTCMonth() + 1, e.getUTCDate(), e.getUTCHours(), e.getUTCMinutes()
    );
    lines.push(`DTSTART;TZID=${anchor.timezone.id}:${start}`);
    lines.push(`DTEND;TZID=${anchor.timezone.id}:${end}`);
  } else {
    lines.push(`DTSTART:${icsUtc(at)}`);
    lines.push(`DTEND:${icsUtc(new Date(at.getTime() + durationMinutes * 60000))}`);
  }

  if (recurring) lines.push('RRULE:FREQ=WEEKLY');

  lines.push(`SUMMARY:${esc(title)}`);

  const anchorNote = anchor
    ? `Anchored to ${anchor.city.name} (${anchor.timezone.id}) — this local time stays fixed across DST transitions.`
    : 'Anchored to UTC — this absolute time stays fixed; local times shift with DST.';

  const description = [
    buildTimeBreakdown(slots, utcHour, date),
    '',
    anchorNote,
    '',
    'Planned with https://timezonemeetingplanner.com',
  ].join('\n');

  lines.push(`DESCRIPTION:${esc(description)}`);
  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.map(fold).join('\r\n') + '\r\n';
}

function icsUtc(d: Date): string {
  return (
    icsDateTime(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes()) + 'Z'
  );
}

function uid(at: Date): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${at.getTime()}-${rand}@timezonemeetingplanner.com`;
}
